import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Link,
  Badge,
  styled,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { BookRelease, Publisher } from '../types/book';
import BookIcon from '@mui/icons-material/Book';
import { publisherAPI, bookReleaseAPI } from '../api/api';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from 'react-router-dom';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom styled component for the day with badge
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 8,
    top: 8,
    border: `1px solid ${theme.palette.secondary.light}`,
    padding: 0,
    minWidth: 'auto',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main,
  },
}));

// ServerDay component following MUI example
const ServerDay = (props: PickersDayProps<Dayjs> & { highlightedDays?: Dayjs[], currentMonth?: Dayjs }) => {
  const { highlightedDays = [], day, currentMonth, ...other } = props;
  
  const isHighlighted = highlightedDays.some(
    (highlightedDay) =>
      highlightedDay.date() === day.date() &&
      highlightedDay.month() === day.month() &&
      highlightedDay.year() === day.year()
  );

  // Only show badge if the day is in the current month being displayed
  const isCurrentMonth = currentMonth ? day.month() === currentMonth.month() : true;

  // Don't show badge if this is the selected date
  const shouldShowBadge = isHighlighted && isCurrentMonth && !props.selected;

  return (
    <StyledBadge
      overlap="circular"
      badgeContent={shouldShowBadge ? <BookIcon sx={{ fontSize: '0.5rem', color: 'primary.main' }} /> : undefined}
      color="default"
    >
      <PickersDay {...other} day={day} />
    </StyledBadge>
  );
};

const Calendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() => dayjs());
  const [selectedPublisher, setSelectedPublisher] = useState<string>('all');
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => dayjs());
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [releases, setReleases] = useState<BookRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [publishersResponse, releasesResponse] = await Promise.all([
          publisherAPI.getPublishers(),
          bookReleaseAPI.getBookReleases()
        ]);
        setPublishers(publishersResponse);
        setReleases(releasesResponse);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePublisherChange = (event: SelectChangeEvent) => {
    setSelectedPublisher(event.target.value);
  };

  // Filter releases based on selected publisher
  const filteredReleases = useMemo(() => {
    return releases.filter((release) => {
      if (selectedPublisher === 'all') return true;
      return release.publisher._id === selectedPublisher;
    });
  }, [selectedPublisher, releases]);

  // Get releases for the selected date
  const releasesOnSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return filteredReleases.filter((release) => {
      const releaseDate = dayjs(release.releaseDate).tz(dayjs.tz.guess());
      return (
        releaseDate.date() === selectedDate.date() &&
        releaseDate.month() === selectedDate.month() &&
        releaseDate.year() === selectedDate.year()
      );
    });
  }, [selectedDate, filteredReleases]);

  // Get all dates with releases for the badge display
  const highlightedDays = useMemo(() => {
    return filteredReleases.map(release => 
      dayjs(release.releaseDate).tz(dayjs.tz.guess())
    );
  }, [filteredReleases]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1, md: 3 }, 
        p: { xs: 1, md: 2 },
        flexDirection: { xs: 'column', md: 'row' },
        '& .MuiPickersCalendarHeader-root': {
          marginBottom: 2,
        },
      }}>
        <Box sx={{ flex: 1 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 1, sm: 2 }, 
              borderRadius: { xs: 1, sm: 2 }, 
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            }}
          >
            <DateCalendar
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              onMonthChange={(newMonth) => setCurrentMonth(newMonth)}
              slots={{
                day: ServerDay,
              }}
              slotProps={{
                day: {
                  highlightedDays,
                  currentMonth,
                } as any,
              }}
              sx={{
                '& .MuiPickersDay-root': {
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '0.9rem',
                  width: 36,
                  height: 36,
                  margin: '0 2px',
                },
                '& .MuiPickersDay-today': {
                  borderColor: 'primary.main',
                  color: '#D4AF37',
                  fontWeight: 'bold',
                },
                '& .MuiPickersDay-root.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '& .MuiPickersCalendarHeader-root': {
                  color: '#D4AF37',
                },
                '& .MuiPickersCalendarHeader-label': {
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  color: '#D4AF37',
                },
                '& .MuiPickersCalendarHeader-labelContainer': {
                  color: '#D4AF37',
                },
                '& .MuiPickersCalendarHeader-switchViewButton': {
                  color: '#D4AF37',
                },
                '& .MuiPickersCalendarHeader-switchViewIcon': {
                  color: '#D4AF37',
                },
                '& .MuiPickersArrowSwitcher-root': {
                  color: '#D4AF37',
                },
                '& .MuiPickersArrowSwitcher-button': {
                  color: '#D4AF37',
                },
              }}
            />
          </Paper>
        </Box>
        <Box sx={{ flex: 2, mt: { xs: 2, md: 0 } }}>
          <FormControl 
            fullWidth 
            sx={{ 
              mb: { xs: 2, md: 2 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D4AF37',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D4AF37',
                },
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Playfair Display", serif',
                '&.Mui-focused': {
                  color: '#D4AF37',
                },
              },
              '& .MuiSelect-select': {
                fontFamily: '"Playfair Display", serif',
              },
              '& .MuiSvgIcon-root': {
                color: '#D4AF37',
              },
            }}
          >
            <InputLabel>Publisher</InputLabel>
            <Select
              value={selectedPublisher}
              label="Publisher"
              onChange={handlePublisherChange}
            >
              <MenuItem value="all">All Publishers</MenuItem>
              {publishers.map((publisher) => (
                <MenuItem key={publisher._id} value={publisher._id}>
                  {publisher.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography 
            variant="h6" 
            sx={{ 
              mb: { xs: 1, md: 1.5 }, 
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Releases on {selectedDate?.format('MMMM D, YYYY')}
          </Typography>

          {releasesOnSelectedDate.length === 0 ? (
            <Typography 
              color="text.secondary" 
              sx={{ 
                fontStyle: 'italic',
                fontFamily: '"Playfair Display", serif',
              }}
            >
              No releases scheduled for this date.
            </Typography>
          ) : (
            <Grid container spacing={{ xs: 1, md: 1.5 }}>
              {releasesOnSelectedDate.map((release) => (
                <Grid item xs={12} key={release._id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/releases/${release._id}`)}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 1.5 } }}>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        {release.coverImage && (
                          <Box
                            component="img"
                            src={release.coverImage}
                            alt={`${release.title} cover`}
                            sx={{
                              width: 200,
                              height: 250,
                              objectFit: 'cover',
                              borderRadius: 1,
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h5" 
                            gutterBottom 
                            sx={{ 
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            {release.title}
                            <Typography 
                              color="text.secondary" 
                              gutterBottom 
                              sx={{ 
                                fontStyle: 'italic',
                                fontFamily: '"Playfair Display", serif',
                              }}
                            >
                              by {release.author}
                            </Typography>
                          </Typography>
                          <Typography 
                            variant="body2" 
                            paragraph
                            sx={{ 
                              fontFamily: '"Playfair Display", serif',
                            }}
                          >
                            {release.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography 
                        variant="h6" 
                        paragraph
                        sx={{ 
                          fontFamily: '"Playfair Display", serif',
                          color: 'text.secondary',
                        }}
                      >
                        {release.publisher.name}
                        <Typography 
                          variant="body2" 
                          paragraph
                          sx={{ 
                            fontFamily: '"Playfair Display", serif',
                            color: 'text.secondary',
                          }}
                        >
                          {(() => {
                            const utcDate = dayjs(release.releaseDate).utc();
                            const isTimeSet = utcDate.hour() !== 0 || utcDate.minute() !== 0;
                            const localDate = utcDate.tz(dayjs.tz.guess());
                            return isTimeSet 
                              ? `${localDate.format('MMMM D, YYYY[,] h:mm A')} ${dayjs.tz.guess().split('/')[1].replace('_', ' ')}`
                              : localDate.format('MMMM D, YYYY');
                          })()}
                          {release.price && `, ${release.price} ${release.currency}`}
                        </Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: { xs: 1, md: 1.5 } }}>
                        <Chip
                          label={release.editionType}
                          color="primary"
                          size="small"
                          sx={{ 
                            borderRadius: 1,
                            fontFamily: '"Playfair Display", serif',
                          }}
                        />
                        <Chip
                          label={release.status}
                          color={release.status === 'Available' ? 'success' : 'default'}
                          size="small"
                          sx={{ 
                            borderRadius: 1,
                            fontFamily: '"Playfair Display", serif',
                          }}
                        />
                        {release.preOrderLink && (
                          <Link
                            href={release.preOrderLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              color: '#D4AF37',
                              textDecoration: 'none',
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 500,
                              display: 'inline-block',
                              position: 'relative',
                              alignSelf: 'flex-end',
                              marginLeft: 'auto',
                              marginRight: '10px',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                width: '0%',
                                height: '2px',
                                bottom: -2,
                                left: 0,
                                backgroundColor: '#D4AF37',
                                transition: 'width 0.3s ease',
                              },
                              '&:hover': {
                                color: '#B38F2E',
                                '&::after': {
                                  width: '100%',
                                  backgroundColor: '#B38F2E',
                                },
                              },
                            }}
                          >
                            Pre-order Link
                          </Link>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Calendar;